// @ts-strict-ignore
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';
import { InventoryUpdate } from '../../services/store/inventories/inventory-save-manager.service';
import { routerEffectsActions } from '../router/router.actions';
import {
  inventoriesEffectsActions,
  inventoriesFacadeActions,
  inventorySaveManagerActions,
} from './inventories.actions';
import { InventoriesState } from './inventories.types';
import { createVariableSaveQueueId } from './utils/create-variable-save-queue-id';

export const inventoriesInitialState: InventoriesState = {
  inventories: null,
  inventoriesLoading: false,
  inventoriesLoadingError: null,
  inventoriesSaveError: [],
  inventoriesSaving: [],
  // deprecated, will be removed once all old inventory components are removed
  variablesSaving: [],
  variablesSaveError: [],
  variablesQueuedForSave: {},
  variableLastSaved: null,
  variablesLogs: {},
  monetaryVariableIds: new Map<string, boolean>(),
};

export const inventoriesReducer = createImmerReducer(
  inventoriesInitialState,

  on(inventoriesEffectsActions.loadInventoriesStart, (state) => ({
    ...state,
    inventories: null,
    inventoriesLoading: true,
    inventoriesLoadingError: null,
  })),

  on(inventoriesEffectsActions.loadedInventories, (state, { inventories }) => {
    return {
      ...state,
      inventories: inventories,
      inventoriesLoading: false,
      inventoriesLoadingError: null,
    };
  }),

  on(inventoriesEffectsActions.loadingInventoriesFailed, (state, { error }) => ({
    ...state,
    inventoriesLoading: false,
    inventoriesLoadingError: error,
  })),

  on(inventoriesEffectsActions.resetInventories, (state) => ({
    ...state,
    inventories: null,
    inventoriesLoading: true,
    inventoriesLoadingError: null,
  })),

  on(inventorySaveManagerActions.saveInventoryValueSuccess, (state, { values }) => {
    const newValuesByInventoryId = groupValuesByInventoryId(values);

    return applyValuesToInventory(state, newValuesByInventoryId);
  }),
  on(inventorySaveManagerActions.saveInventoryValueError, (state, { values }) => {
    const oldValuesByInventoryId = groupValuesByInventoryId(values);
    const newState = applyValuesToInventory(state, oldValuesByInventoryId);

    return { ...newState, inventoriesSaveError: Object.keys(oldValuesByInventoryId) };
  }),

  // on(inventoriesEffectsActions.savedInventoryMonetaryValue, (state, { monetaryValue }) => {
  //   if (!state.inventories) {
  //     return state;
  //   }

  //   const monetaryValues = state.inventories.monetaryVariablesValues;
  //   const matchingMonetaryValues = monetaryValues[monetaryValue.variableDefinitionId];

  //   if (!matchingMonetaryValues || !matchingMonetaryValues.length) {
  //     return state;
  //   }

  //   const updatedMonetaryValues = matchingMonetaryValues.map((value) => {
  //     if (value.id === monetaryValue.id) {
  //       return monetaryValue;
  //     }
  //     return value;
  //   });

  //   const updatedInventories = {
  //     ...state.inventories,
  //     monetaryVariablesValues: {
  //       ...monetaryValues,
  //       [monetaryValue.variableDefinitionId]: updatedMonetaryValues,
  //     },
  //   };

  //   return {
  //     ...state,
  //     inventories: updatedInventories,
  //   };
  // }),

  on(routerEffectsActions.routeBoardIdChanged, (state) => {
    const wereVariablesLoadedPreviously = !!state.inventories;
    return {
      ...inventoriesInitialState,
      inventoriesLoading: wereVariablesLoadedPreviously,
    };
  }),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.loadedVariableLogs, (state, { valueId, variableDetails }) => ({
    ...state,
    variablesLogs: { ...state.variablesLogs, [valueId]: variableDetails },
  })),

  /**
   * @deprecated
   */
  on(inventoriesFacadeActions.addMonetaryVariableIDs, (state, { ids }) => ({
    ...state,
    monetaryVariableIds: ids,
  })),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.saveVariable, (state, { variableSaveEnvelope }) => {
    const { boardId, variableNameId } = variableSaveEnvelope.requestParams;
    const queueId = createVariableSaveQueueId(boardId, variableNameId);
    const variablesSaving = [...new Set([...state.variablesSaving, queueId])];

    return {
      ...state,
      variablesSaving,
    };
  }),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.queueVariableSave, (state, { variableSaveEnvelope }) => {
    const { boardId, variableNameId } = variableSaveEnvelope.requestParams;
    const queueId = createVariableSaveQueueId(boardId, variableNameId);
    const variablesQueuedForSave = { ...state.variablesQueuedForSave, [queueId]: variableSaveEnvelope };

    return {
      ...state,
      variablesQueuedForSave,
    };
  }),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.saveQueuedVariable, (state, { variableSaveEnvelope }) => {
    const { variableNameId, boardId } = variableSaveEnvelope.requestParams;
    const variablesSaving = [...new Set([...state.variablesSaving, variableNameId])];

    const queueId = createVariableSaveQueueId(boardId, variableNameId);
    const { [queueId]: _, ...variablesQueuedForSave } = state.variablesQueuedForSave;

    return {
      ...state,
      variablesSaving,
      variablesQueuedForSave,
    };
  }),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.savedVariable, (state, { variable }) => {
    const { saveId, queueId, ...clientVariable } = variable;
    const inventoryId = clientVariable.inventoryId;
    const inventory = state.inventories?.get(inventoryId);
    if (!inventory) {
      return state;
    }

    const newState = applyValuesToInventory(state, {
      [inventoryId]: <InventoryUpdate[]>[
        {
          valueId: variable.valueId,
          value: variable.metricValue,
          oldValue: 0,
          inventoryId: variable.inventoryId,
          variableId: variable.name,
        },
      ],
    });

    const variablesSaving = state.variablesSaving.filter((qId) => qId !== queueId);
    const variablesSaveError = state.variablesSaveError.filter((qId) => qId !== queueId);
    return {
      ...newState,
      variablesSaving,
      variablesSaveError,
      variableLastSaved: { ...clientVariable, saveId },
    };
  }),

  /**
   * @deprecated
   */
  on(inventoriesEffectsActions.savingVariableFailed, (state, { queueId }) => {
    const variablesSaving = state.variablesSaving.filter((qId) => qId !== queueId);
    const variablesSaveError = [...new Set([...state.variablesSaveError, queueId])];

    return {
      ...state,
      variablesSaving,
      variablesSaveError,
    };
  })
);

const groupValuesByInventoryId = (values: InventoryUpdate[]): { [key: string]: InventoryUpdate[] } => {
  return values.reduce(
    (acc, value) => {
      if (!acc[value.inventoryId]) {
        acc[value.inventoryId] = [];
      }
      acc[value.inventoryId].push(value);
      return acc;
    },
    {} as { [key: string]: InventoryUpdate[] }
  );
};

const applyValuesToInventory = (state: InventoriesState, valuesByInventoryId: { [key: string]: InventoryUpdate[] }) => {
  const newInventories = new Map(state.inventories);

  for (const inventoryId in valuesByInventoryId) {
    const inventory = newInventories.get(inventoryId);
    if (!inventory) {
      continue;
    }

    const updatedVariables = inventory.variables.map((variable: any) => {
      const newValue = valuesByInventoryId[inventoryId].find((v) => v.variableId === variable.name);
      if (newValue) {
        return {
          ...variable,
          value: newValue.value,
          metricValue: newValue.value,
        };
      }
      return variable;
    });

    newInventories.set(inventoryId, {
      ...inventory,
      variables: updatedVariables,
    });
  }

  return {
    ...state,
    inventories: newInventories,
  };
};
